import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Badge,
  Box,
  Button,
  Flex,
  Spinner,
  Stack,
  Text,
  useColorModeValue,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import MDEditor from '@uiw/react-md-editor';
import { useEffect, useMemo, useRef, useState } from 'react';
import remarkBreaks from 'remark-breaks';
import PageCard from '../../../components/page-card';
import InlineErrorState from '../../../components/states/InlineErrorState';
import { translate } from '../../../i18n';
import { useGetOrganizationCgu } from '../../organization/hooks/useGetOrganizationCgu';
import { useSelectedOrganization } from '../../organization/hooks/useSelectedOrganization';
import { useUpdateOrganizationCgu } from '../../organization/hooks/useUpdateOrganizationCgu';
import { useGetUserMembershipTypes } from '../../user-management/hooks/useGetUserMembershipTypes';

/**
 * Org-level CGU editor.
 *
 * - Markdown editor with toolbar + live preview (@uiw/react-md-editor).
 * - Pre-fills with the currently-saved cguText via GET /organizations/:id/cgu.
 * - Save button is disabled until the buffer differs from the saved value.
 * - Saving an empty buffer is rejected by the UI: clearing the CGU is a separate
 *   "Remove CGU" destructive action with its own confirmation.
 * - When N membership types have their own CGU override set (cguVersion > 0
 *   AND some text has historically been there), the save flow surfaces a
 *   warning that those overrides will NOT inherit the new org CGU.
 */
export default function CguPolicyCard() {
  const { selectedOrganization } = useSelectedOrganization();
  const orgId = selectedOrganization?.id ?? '';
  const toast = useToast();
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const subtleColor = useColorModeValue('secondaryGray.600', 'secondaryGray.400');
  const editorColorMode = useColorModeValue<'light' | 'dark'>('light', 'dark');

  const { data: orgCgu, isLoading: isLoadingCgu } = useGetOrganizationCgu(orgId);
  /**
   * `isError` is load-bearing here, and not for the usual reason.
   *
   * Every other site in this class renders a failed read as an empty list —
   * a confident false statement. This one does something worse: it uses the
   * list to decide whether to SHOW A CONFIRMATION. `overrideTypeCount` below
   * is derived from `membershipTypes`, and `handleSaveClick` opens the drift
   * dialog only when that count is > 0. A refused or failed
   * `GET organizations/:id/membershipTypes` leaves `data === undefined`, so
   * the count is 0, so the dialog is skipped and the operator publishes new
   * org terms never having been told that N membership types carry their own
   * overrides and will not inherit them. A check whose failure branch
   * proceeds without the check reads exactly like a check.
   *
   * The route carries `AdminAuthGuard` + `hasOrganizationAccess`
   * (`api/src/organization/organization.controller.ts`), so a stale token or
   * an operator acting on an org they are not a member of is refused — and
   * the CGU write is a different route that may well still succeed.
   */
  const { data: membershipTypes = [], isError: isMembershipTypesError } =
    useGetUserMembershipTypes(orgId);
  const { mutateAsync: saveCgu, isLoading: isSaving } =
    useUpdateOrganizationCgu(orgId);

  // Buffer the editor's current value separately from the saved value so we can
  // diff for the dirty check.
  const [buffer, setBuffer] = useState<string>('');
  const lastSyncedTextRef = useRef<string | null>(null);

  // Sync buffer with the fetched cguText on first load and after a save flushes.
  useEffect(() => {
    if (!orgCgu) return;
    if (lastSyncedTextRef.current === orgCgu.cguText) return;
    setBuffer(orgCgu.cguText ?? '');
    lastSyncedTextRef.current = orgCgu.cguText;
  }, [orgCgu?.cguText]);

  // Membership types that currently override the org CGU. We use cguVersion > 0
  // as the heuristic ("override has been set at some point") since the actual
  // override text isn't on the type DTO. The drift warning is informational —
  // operators with stale-overridden types should review them after editing org
  // CGU. Counting cguVersion > 0 over-counts (includes types that overrode
  // then cleared); a precise check would call GET /cgu per type.
  const overrideTypeCount = useMemo(
    () =>
      membershipTypes.filter(
        (t) => (t.cguVersion ?? 0) > 0 && !t.isOrganizationDefault,
      ).length,
    [membershipTypes],
  );

  const driftDialog = useDisclosure();
  const removeDialog = useDisclosure();
  const removeCancelRef = useRef<HTMLButtonElement>(null);

  const savedText = orgCgu?.cguText ?? '';
  const isDirty = buffer !== savedText;
  const isEmpty = buffer.trim().length === 0;
  // Save is allowed on a dirty buffer that is non-empty. Empty + dirty means
  // "operator cleared the textarea" — we route that through Remove CGU instead
  // so they can't accidentally disable enforcement.
  const canSave = isDirty && !isEmpty;

  const handleSaveClick = () => {
    if (!canSave) return;
    if (overrideTypeCount > 0) {
      driftDialog.onOpen();
      return;
    }
    void doSave();
  };

  const doSave = async () => {
    try {
      await saveCgu({ cguText: buffer });
      lastSyncedTextRef.current = buffer;
      toast({
        title: translate('common.save'),
        status: 'success',
        duration: 2000,
        position: 'top-right',
      });
    } catch {
      toast({
        title: translate('common.error'),
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
      });
    } finally {
      driftDialog.onClose();
    }
  };

  const doRemove = async () => {
    try {
      await saveCgu({ cguText: null });
      setBuffer('');
      lastSyncedTextRef.current = null;
      toast({
        title: translate('cgu.removed'),
        status: 'success',
        duration: 2000,
        position: 'top-right',
      });
    } catch {
      toast({
        title: translate('common.error'),
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
      });
    } finally {
      removeDialog.onClose();
    }
  };

  if (!selectedOrganization) return null;

  return (
    <>
      <PageCard title={translate('cgu.title')}>
        <Box p={4}>
          <Stack spacing={3}>
            <Flex align="center" gap={3}>
              <Text color={textColor} fontSize="sm">
                {translate('cgu.description')}
              </Text>
              {orgCgu && (
                <Badge colorScheme={savedText ? 'green' : 'gray'}>
                  v{orgCgu.cguVersion}
                  {savedText ? '' : ` · ${translate('cgu.not_configured')}`}
                </Badge>
              )}
            </Flex>

            {/*
              * The override check could not run. Said out loud rather than
              * silently rounded to "no type overrides the org terms", which is
              * what an empty list means everywhere else on this page. Save is
              * deliberately NOT blocked: the drift dialog is informational
              * ("Review them after saving") and never prevented anything, so
              * refusing to let an operator edit their own terms because an
              * unrelated read failed would be a worse regression than the one
              * this fixes. What they get instead is the truth — we do not know.
              */}
            {isMembershipTypesError && (
              <InlineErrorState
                message={translate('userMembership.error_loading')}
              />
            )}

            {isLoadingCgu ? (
              <Flex justify="center" py={6}>
                <Spinner />
              </Flex>
            ) : (
              <Box data-color-mode={editorColorMode}>
                <MDEditor
                  value={buffer}
                  onChange={(v) => setBuffer(v ?? '')}
                  height={400}
                  preview="live"
                  textareaProps={{
                    placeholder: translate('cgu.editor_placeholder'),
                    'aria-label': translate('cgu.title'),
                  }}
                  previewOptions={{ remarkPlugins: [remarkBreaks] }}
                />
              </Box>
            )}

            <Text fontSize="xs" color={subtleColor}>
              {translate('cgu.editor_hint')}
            </Text>

            <Flex gap={3} pt={2} wrap="wrap">
              <Button
                colorScheme="brand"
                onClick={handleSaveClick}
                isDisabled={!canSave}
                isLoading={isSaving && !removeDialog.isOpen}
              >
                {translate('common.save')}
              </Button>
              <Button
                colorScheme="red"
                variant="outline"
                onClick={removeDialog.onOpen}
                isDisabled={!savedText || isSaving}
              >
                {translate('cgu.remove')}
              </Button>
              {isDirty && isEmpty && (
                <Text fontSize="sm" color="red.500" alignSelf="center">
                  {translate('cgu.empty_save_blocked')}
                </Text>
              )}
            </Flex>
          </Stack>
        </Box>
      </PageCard>

      {/* Drift warning: "N membership types have their own CGU and won't inherit this update" */}
      <AlertDialog
        isOpen={driftDialog.isOpen}
        onClose={driftDialog.onClose}
        leastDestructiveRef={removeCancelRef}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader>{translate('cgu.drift_title')}</AlertDialogHeader>
            <AlertDialogBody>
              <Text>
                {translate('cgu.drift_body', { count: overrideTypeCount })}
              </Text>
              <Text mt={2} fontSize="sm" color={subtleColor}>
                {translate('cgu.drift_hint')}
              </Text>
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button
                ref={removeCancelRef}
                onClick={driftDialog.onClose}
                isDisabled={isSaving}
              >
                {translate('common.cancel')}
              </Button>
              <Button
                colorScheme="brand"
                ml={3}
                onClick={doSave}
                isLoading={isSaving}
              >
                {translate('cgu.save_anyway')}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      {/* Destructive "Remove CGU" — distinct from save-empty so it can never be a slip. */}
      <AlertDialog
        isOpen={removeDialog.isOpen}
        onClose={removeDialog.onClose}
        leastDestructiveRef={removeCancelRef}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader>{translate('cgu.remove_title')}</AlertDialogHeader>
            <AlertDialogBody>
              <Text>{translate('cgu.remove_body')}</Text>
              <Text mt={2} fontSize="sm" color={subtleColor}>
                {translate('cgu.remove_hint')}
              </Text>
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button
                ref={removeCancelRef}
                onClick={removeDialog.onClose}
                isDisabled={isSaving}
              >
                {translate('cgu.edit_instead')}
              </Button>
              <Button
                colorScheme="red"
                ml={3}
                onClick={doRemove}
                isLoading={isSaving}
              >
                {translate('cgu.confirm_remove')}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
}
